import { DOCUMENT, NgIf, registerLocaleData } from "@angular/common";
import locale from "@angular/common/locales/nl-BE";
import { Component, Inject, OnInit } from "@angular/core";
import {
  ActivatedRoute,
  NavigationEnd,
  NavigationStart,
  Router,
  RouterOutlet,
} from "@angular/router";
import { NgSelectConfig } from "@ng-select/ng-select";
import * as Sentry from "@sentry/angular";
import { filter, forkJoin } from "rxjs";
import "../assets/fonts/OpenSans-Bold-normal.js";
import "../assets/fonts/OpenSans-Regular-normal.js";
import { environment } from "../environments/environment";
import { NavbarComponent } from "./_shared/components/layout/navbar/navbar.component";
import { MenuItem } from "./_shared/components/layout/sidenav/models/menuItem";
import { SidenavComponent } from "./_shared/components/layout/sidenav/sidenav.component";
import { ClickOutsideDirective } from "./_shared/directives/click-outside.directive";
import { BeaconService } from "./_shared/services/beacon.service";
import { GlobalVariables } from "./_shared/services/global.service";
import { WebServiceWorker } from "./_shared/services/service-worker.service";
import { createRoutePath } from "./_shared/utils/route-util";
import { AppTemplateComponent } from "./_template/components/app.component";
import { AccountingPaths } from "./accounting/accounting-routes";
import { StateOfAssetsPaths } from "./accounting/state-of-assets-routes";
import { ActivityPaths } from "./activity/activity-routes";
import { AppPaths } from "./app-routes";
import { CommunicationPaths } from "./communication/communication-routes";
import { DocumentsPaths } from "./documents/documents-routes";
import { ExpensesPaths } from "./expenses/expenses-routes";
import { FederationPaths } from "./federation/federation-routes";
import { FormsPaths } from "./forms/forms-routes";
import { JobsPaths } from "./jobs/jobs-routes";
import { JobPostcodeService } from "./jobs/services/job-postcode.service";
import { MeetingsPaths } from "./meetings/meetings-routes";
import { MembersPaths } from "./members/members-routes";
import { MenuService } from "./menu/services/menu.service";
import { Federations } from "./organisation/models/federation";
import { SubscriptionTypeEnum } from "./organisation/models/subscription";
import { OrganizationPaths } from "./organisation/organisation-routes";
import { OrganizationRelationService } from "./organisation/services/organization-relation.service";
import { StartsettingsService } from "./organisation/services/startsettings.service";
import { SubscriptionService } from "./organisation/services/subscription.service";
import { PersonsPaths } from "./person/person-routes";
import { ProductPaths } from "./product/product.routes";
import { ProductGroupService } from "./product/services/product-group.service";
import { LocalStorageResetComponent } from "./public/pages/local-storage-reset/local-storage-reset.component";
import { TasksPaths } from "./tasks/tasks-routes";
import { AuthService } from "./users/services/auth.service";
import { UserService } from "./users/services/user.service";
import { VolunteersPaths } from "./volunteers/volunteers-routes";
import { VzwHelperPaths } from "./vzw-helper/vzw-helper-routes";
import { VrijwilligerswerkPaths } from "./weldoeners/vrijwilligerswerk-routes";
import { WeldoenersPaths } from "./weldoeners/weldoeners-routes";
import { WidgetsPaths } from "./widgets/widgets-routes";

registerLocaleData(locale);

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  standalone: true,
  imports: [
    NgIf,
    SidenavComponent,
    NavbarComponent,
    RouterOutlet,
    ClickOutsideDirective,
    LocalStorageResetComponent,
  ],
})
export class AppComponent extends AppTemplateComponent implements OnInit {
  title = "Assist";
  signedIn = false;
  loaded = false;
  showmessage = false;
  global = GlobalVariables;
  federations = Federations;
  hasKanikhelpen = false;
  version = environment.appVersion;

  //subscriptionTypeIds
  //1	Assist PLUS
  //4	Assist START
  //5	Assist FREE

  menuItems: MenuItem[];

  isErrorSolver = false;

  constructor(
    @Inject(DOCUMENT) private doc: Document,
    private route: ActivatedRoute,
    private userService: UserService,
    private authService: AuthService,
    private config: NgSelectConfig,
    private subscriptionService: SubscriptionService,
    private startsettingService: StartsettingsService,
    private worker: WebServiceWorker,
    private jobPostcodeService: JobPostcodeService,
    protected productGroupService: ProductGroupService,
    private menuService: MenuService,
    private beacon: BeaconService,
    private router: Router,
    protected orgRelationService: OrganizationRelationService
  ) {
    super(doc);
    super.ngSelectTranslate(config);
    this.global.isWhiteLabelVsvw = doc.location.hostname.includes("vrijwilligerswerk.be");
    if (this.global.isWhiteLabelVsvw) {
      this.loadWhiteLabelCss();
    }
    this.menuItems = [
      {
        name: "Welkom" + (this.global.isWhiteLabelVsvw ? "" : " bij Assist"),
        route: "/welkom",
        icon: "fa-house",
      },
    ];
  }

  private loadWhiteLabelCss(): void {
    const link = this.doc.createElement("link");
    link.rel = "stylesheet";
    link.href = "assets/css/white-label.css";
    this.doc.head.appendChild(link);

    this.doc.title = "Mijn VSVw-Account";

    const favicon = this.doc.querySelector<HTMLLinkElement>("link[rel='icon']");
    if (favicon) {
      favicon.href = "assets/img/icon-vsvw.png";
      favicon.type = "image/png";
    }
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      console.log("app"); // { orderby: "price" }
      console.log(params); // { orderby: "price" }
      if (params["accessKey"]) {
        localStorage.setItem("accessKey", params["accessKey"]);
        window.location.href = window.location.href.substr(0, window.location.href.indexOf("?"));
        window.location.reload();
      } else {
        var accessKey = this.getAccessKeyFromQueryString();
        if (accessKey) {
          localStorage.setItem("accessKey", accessKey);
          window.location.href = window.location.href.substr(0, window.location.href.indexOf("?"));
          window.location.reload();
        } else {
          setTimeout(() => {
            this.userService.isSignedIn().subscribe((signedIn) => {
              this.signedIn = signedIn;
            });
          }, 500);
          setInterval(() => {
            this.userService.isSignedIn().subscribe((signedIn) => {
              this.signedIn = signedIn;
            });
          }, 60000);
        }
      }
    });

    setTimeout(() => {
      this.showmessage = true;
    }, 3000);

    this.userService.getCurrent().subscribe((user) => {
      this.global.user = user;
      this.beacon.init().then(() => {
        this.beacon.identify({
          name: user.FirstName + " " + user.LastName,
          email: user.Email,
          company: user.OrganisationName,
          orgId: user.OrganisationId,
        });
      });
      Sentry.setUser({
        organization: user.OrganisationName,
        organizationId: user.OrganisationId,
        federationId: user.FederationId,
        id: user.Id,
      });
      Sentry.setTag("organizationId", user.OrganisationId);
      Sentry.setTag("organizationName", user.OrganisationName);
    });

    this.jobPostcodeService.getAllOdata("").subscribe((postcodes) => {
      this.hasKanikhelpen = postcodes.Count > 0;

      this.subscriptionService.getCurrent().subscribe((subscription) => {
        this.global.subscription = subscription;

        // Auth-token meegeven in hash-fragment zodat opnieuw inloggen niet nodig is.
        // Formaat: #/route?accessKey=TOKEN — wordt door getAccessKeyFromQueryString() afgehandeld.
        const accessKey = localStorage.getItem("accessKey");
        const hashWithToken = window.location.hash + (accessKey ? `?accessKey=${accessKey}` : "");

        // Redirect FREE-organisaties van assistonline.eu naar vrijwilligerswerk.be
        if (
          window.location.hostname === "nieuw.assistonline.eu" &&
          subscription.SubscriptionTypeId === SubscriptionTypeEnum.FREE &&
          subscription.Organization.FederationId == Federations.VlaamsSteunpuntVrijwilligerswerk
        ) {
          window.location.href = environment.vrijwilligerswerkUrl + hashWithToken;
          return;
        }

        // Redirect niet-FREE organisaties van vrijwilligerswerk.be naar assistonline.eu
        if (
          this.global.isWhiteLabelVsvw &&
          (subscription.SubscriptionTypeId !== SubscriptionTypeEnum.FREE ||
            subscription.Organization.FederationId != Federations.VlaamsSteunpuntVrijwilligerswerk)
        ) {
          window.location.href = environment.assistUrl + hashWithToken;
          return;
        }

        this.beacon.init().then(() => {
          this.beacon.identify({
            "abo-type": subscription.SubscriptionType.Name,
            "abo-status": subscription.SubscriptionStatus.Abbreviation,
            federation: subscription.Organization.FederationId,
          });
        });

        if (subscription.OrganizationId) {
          this.startsettingService.getCurrent().subscribe((data) => {
            this.global.startSettings = data;
            this.global.organization = data.Organization;

            this.setMenuItems();
            this.loaded = true;
          });
        } else {
          this.global.organization = subscription.Organization;
          this.setMenuItems();
          this.loaded = true;
        }
      });
    });
    this.router.events.subscribe((ev) => {
      if (ev instanceof NavigationStart) {
        this.beacon.close();
        this.beacon.suggest([]);
      }
    });
  }

  setMenuItems() {
    // - - - subscriptionTypeId - - -
    // 1 = PLUS
    // 4 = START
    // 5 = FREE

    // - - - federationIds - - -
    // 2 = Vlaamse Zwemfederatie
    // 1013 = Vlaamse Sportfederatie
    // 1030 = Lokale besturen

    forkJoin([
      this.menuService.getMenuTemplate(),
      this.orgRelationService.isChild(11458, 57),
    ]).subscribe(([menu, isVsvwChild]) => {
      menu = menu.filter(
        (menuItem) =>
          menuItem.Visible === true && menuItem.LinkNew != null && menuItem.LinkNew !== ""
      );
      this.menuItems.push(
        ...menu.map((menuItem) => ({
          name: menuItem.Name,
          route: menuItem.LinkNew,
          icon: menuItem.Icon ?? "fa-house",
          subItems: menuItem.Children.filter(
            (child) => child.Visible === true && menuItem.LinkNew != null && menuItem.LinkNew !== ""
          ).map((child) => ({
            name: child.Name,
            route: child.LinkNew,
          })),
        }))
      );
      if (this.menuItems.length === 1 || (this.menuItems.length == 2 && isVsvwChild)) {
        this.loadDefaultMenu();
      }
      if (isVsvwChild) {
        this.menuItems.push({
          name: "V-Wijzer",
          route: createRoutePath([AppPaths.VWijzer]),
          icon: "fa-building-memo",
        });
      }
    });
  }

  loadDefaultMenu() {
    if (
      this.global.organization.FederationId == this.federations.VlaamsSteunpuntVrijwilligerswerk
    ) {
      //if (this.global.subscription.SubscriptionTypeId == 5) {
      //VSVw en FREE
      this.menuItems.push(
        {
          name: "Mijn organisatie",
          route: createRoutePath(AppPaths.Organization),
          icon: "fa-sitemap",
        },
        {
          name: "Vacatures",
          route: createRoutePath([AppPaths.Jobs, JobsPaths.Start]),
          icon: "fa-ballot",
          subItems: [
            {
              name: "Vacatures beheren",
              route: createRoutePath(AppPaths.Jobs),
            },
            {
              name: "Vacature toevoegen",
              route: createRoutePath([AppPaths.Jobs, JobsPaths.Add]),
            },
            {
              name: "Sjablonen beheren",
              route: createRoutePath([AppPaths.Jobs, JobsPaths.Templates]),
            },
            {
              name: "Widgets beheren",
              route: createRoutePath([AppPaths.Widgets, WidgetsPaths.Overview]),
            },
          ],
        },
        {
          name: "Kandidaat-vrijwilligers",
          route: createRoutePath([AppPaths.Volunteers, VolunteersPaths.Candidates]),
          icon: "fa-handshake-angle",
        },
        {
          name: "Contacten",
          icon: "fa-address-book",
          subItems: [
            {
              name: "Alle personen",
              route: createRoutePath(AppPaths.Persons),
            },
          ],
        }
      );
      //}
    }

    if (this.global.organization.FederationId == this.federations.LokaleBesturen) {
      if (this.global.subscription.SubscriptionTypeId == 5 && this.hasKanikhelpen) {
        //FREE en kanikhelpen
        this.menuItems.push(
          {
            name: "Mijn lokaal bestuur",
            route: createRoutePath([AppPaths.Organization, OrganizationPaths.LocalGovernment]),
            icon: "fa-sitemap",
          },
          {
            name: "Vacatures",
            icon: "fa-ballot",
            subItems: [
              {
                name: "Vacatures beheren",
                route: createRoutePath(AppPaths.Jobs),
              },
              {
                name: "Vacature toevoegen",
                route: createRoutePath([AppPaths.Jobs, JobsPaths.Add]),
              },
              {
                name: "Sjablonen beheren",
                route: createRoutePath([AppPaths.Jobs, JobsPaths.Templates]),
              },
              {
                name: "Widgets beheren",
                route: createRoutePath([AppPaths.Widgets, WidgetsPaths.Overview]),
              },
            ],
          },
          {
            name: "Vrijwilligers",
            icon: "fa-handshake-angle",
            subItems: [
              {
                name: "Kandidaten beheren",
                route: createRoutePath([AppPaths.Volunteers, VolunteersPaths.Candidates]),
              },
            ],
          }
        );
      } else {
        this.menuItems.push(
          {
            name: "Mijn lokaal bestuur",
            route: createRoutePath([AppPaths.Organization, OrganizationPaths.LocalGovernment]),
            icon: "fa-sitemap",
          },
          {
            name: "Vrijwilligers",
            icon: "fa-handshake-angle",
            route: createRoutePath([AppPaths.Volunteers, VolunteersPaths.Start]),
            subItems: [
              {
                name: "Snel starten",
                route: createRoutePath([AppPaths.Volunteers, VolunteersPaths.Start]),
              },
              {
                name: "Kandidaten beheren",
                route: createRoutePath([AppPaths.Volunteers, VolunteersPaths.Candidates]),
              },
              {
                name: "Vrijwilligers beheren",
                route: createRoutePath([AppPaths.Volunteers, VolunteersPaths.Overview]),
              },
              {
                name: "Vrijwilliger toevoegen",
                route: createRoutePath([AppPaths.Volunteers, VolunteersPaths.VolunteerAdd]),
              },
              {
                name: "Vrijwilligers importeren",
                route: createRoutePath([AppPaths.Volunteers, VolunteersPaths.Import]),
              },
              {
                name: "Vrijwilligersgroepen beheren",
                route: createRoutePath([AppPaths.Volunteers, VolunteersPaths.Groups]),
              },
              {
                name: "Functies beheren",
                route: createRoutePath([AppPaths.Volunteers, VolunteersPaths.Functions]),
              },
              {
                name: "Interesses beheren",
                route: createRoutePath([AppPaths.Tasks, TasksPaths.Interests]),
              },
              {
                name: "Afsprakennota's beheren",
                route: createRoutePath([AppPaths.Volunteers, VolunteersPaths.AgreementNotes]),
              },
              {
                name: "Rapportage en statistieken",
                route: createRoutePath([AppPaths.Volunteers, VolunteersPaths.Statistics]),
              },
            ],
          },
          {
            name: "Taken en interesses",
            route: createRoutePath([AppPaths.Tasks, TasksPaths.Start]),
            icon: "fa-tasks",
            //federationIds: [2, 1013, 1030],
            subItems: [
              {
                name: "Snel starten",
                route: createRoutePath([AppPaths.Tasks, TasksPaths.Start]),
              },
              {
                name: "Interesses beheren",
                route: createRoutePath([AppPaths.Tasks, TasksPaths.Interests]),
              },
              {
                name: "Personen importeren",
                route: createRoutePath([AppPaths.Persons, PersonsPaths.Import]),
              },
              {
                name: "Polsen naar interesses",
                route: createRoutePath([AppPaths.Persons, PersonsPaths.IntrestCheck]),
              },
              {
                name: "Taken beheren",
                route: createRoutePath([AppPaths.Tasks, TasksPaths.Overview]),
              },
              //{
              //  name: 'Sjablonen beheren',
              //  route: '/tasks/sjablonen',
              //},
              {
                name: "Uitnodigen en werven",
                route: createRoutePath([AppPaths.Tasks, TasksPaths.Invite]),
              },
              {
                name: "Uitgenodigde kandidaten",
                route: createRoutePath([AppPaths.Tasks, TasksPaths.InvitedPersons]),
              },
            ],
          },
          {
            name: "Kostenvergoedingen",
            icon: "fa-money-bill",
            subItems: [
              {
                name: "Kosten beheren",
                route: createRoutePath([AppPaths.Expenses, ExpensesPaths.List]),
              },
              {
                name: "Kostennota's",
                route: createRoutePath([AppPaths.Expenses, ExpensesPaths.Reports]),
              },
              {
                name: "Kostencategorieën",
                route: createRoutePath([AppPaths.Expenses, ExpensesPaths.Categories]),
              },
              {
                name: "Polsen naar kosten",
                route: createRoutePath([AppPaths.Expenses, ExpensesPaths.Check]),
              },
            ],
          },
          {
            name: "Vacatures",
            icon: "fa-ballot",
            subItems: [
              {
                name: "Vacatures beheren",
                route: createRoutePath([AppPaths.Jobs]),
              },
              {
                name: "Vacature toevoegen",
                route: createRoutePath([AppPaths.Jobs, JobsPaths.Add]),
              },
              {
                name: "Sjablonen beheren",
                route: createRoutePath([AppPaths.Jobs, JobsPaths.Templates]),
              },
              {
                name: "Widgets beheren",
                route: createRoutePath([AppPaths.Widgets, WidgetsPaths.Overview]),
              },
            ],
          },
          {
            name: "Formulieren",
            route: createRoutePath(AppPaths.Forms),
            icon: "fa-memo-pad",
            subItems: [
              {
                name: "Inschrijvingsformulieren",
                route: createRoutePath([AppPaths.Forms, FormsPaths.Overview]),
              },
            ],
          }
        );
      }

      if (this.global.subscription.OrganizationId == 17786) {
        this.menuItems.push(
          {
            name: "Documenten",
            icon: "fa-cabinet-filing",
            subItems: [
              {
                name: "Documenten beheren",
                route: createRoutePath(AppPaths.Documents),
              },
              {
                name: "Sjablonen beheren",
                route: createRoutePath([AppPaths.Documents, DocumentsPaths.Templates]),
              },
            ],
          },
          {
            name: "Gesprekken",
            icon: "fa-comments",
            subItems: [
              {
                name: "Gesprekken beheren",
                route: createRoutePath([AppPaths.Meetings, MeetingsPaths.Overview]),
              },
              /*               {
                name: "Kennismakingsgesprekken",
                route: createRoutePath([AppPaths.Meetings, MeetingsPaths.Overview]),
              },
              {
                name: "Evaluatiegesprekken",
                route: createRoutePath([AppPaths.Meetings, MeetingsPaths.Overview]),
              },
              {
                name: "Afscheidsgesprekken",
                route: createRoutePath([AppPaths.Meetings, MeetingsPaths.Overview]),
              },
             */
              {
                name: "Gesprek toevoegen",
                route: createRoutePath([AppPaths.Meetings, MeetingsPaths.Add]),
              },
              {
                name: "Sjablonen beheren",
                route: createRoutePath([AppPaths.Meetings, MeetingsPaths.Templates]),
              },
            ],
          },
          {
            name: "Communicatie",
            icon: "fa-envelope",
            subItems: [
              {
                name: "Nieuwsbrieven",
                route: createRoutePath([
                  AppPaths.Communication,
                  CommunicationPaths.NewsletterOverview,
                ]),
              },
              {
                name: "E-mailsjablonen",
                route: createRoutePath([
                  AppPaths.Communication,
                  CommunicationPaths.MailTemplateList,
                ]),
              },
            ],
          },
          {
            name: "Contacten",
            icon: "fa-address-book",
            subItems: [
              {
                name: "Alle personen",
                route: createRoutePath(AppPaths.Persons),
              },
              {
                name: "Alle organisaties",
                route: createRoutePath(AppPaths.Addresses),
              },
            ],
          }
        );
      }
    } else {
      // - - - OrganizationId - - -
      //  1535 = Vlaamse Zwemfederatie vzw
      // 11458 = Vlaams Steunpunt Vrijwilligerswerk
      // 12148 = Stad Genk (= subsidiedossiers)
      // 26738 = Lokaal bestuur Wuustwezel (= test-abonnement)
      // 12535 = Het Voorbeeld vzw...
      // 17786 = Lokaal bestuur Zonnedorp
      if (
        this.global.subscription.OrganizationId != 1535 &&
        this.global.subscription.OrganizationId != 11458 &&
        this.global.subscription.OrganizationId != 12148 &&
        this.global.subscription.OrganizationId != 26738 &&
        this.global.subscription.OrganizationId != 17786 &&
        this.global.organization.FederationId != this.federations.VlaamsSteunpuntVrijwilligerswerk
      ) {
        this.productGroupService.getAllBasic().subscribe((groups) => {
          //Default menu
          this.menuItems.push({
            name: "Mijn organisatie",
            route: createRoutePath(AppPaths.Organization),
            icon: "fa-sitemap",
          });
          if (groups.length > 0 && this.global.organization.FederationId == Federations.Vlamo) {
            const vlamoSubItems: MenuItem[] = groups.map((group) => ({
              name: group.Name,
              route: createRoutePath([
                AppPaths.Products,
                ProductPaths.GroupPersonProductList(group.Id),
              ]),
            }));

            const orgTypeMap: Record<number, number> = {
              7027: 3, // VLAMO Oost-Vlaanderen
              4465: 4, // VLAMO West-Vlaanderen
              2998: 5, // VLAMO Vlaams-Brabant en Brussel vzw
              6429: 6, // VLAMO Antwerpen
              7029: 7, // VLAMO Limburg
            };
            const orgTypeId = orgTypeMap[this.global.subscription.OrganizationId!];
            if (orgTypeId) {
              vlamoSubItems.push({
                name: "Verenigingen",
                route: createRoutePath([AppPaths.Federation, FederationPaths.Overview(orgTypeId)]),
              });
              vlamoSubItems.push({
                name: "VLAMO-ondersteuning",
                route: createRoutePath([
                  AppPaths.Federation,
                  FederationPaths.FormDocumentStatusList(orgTypeId, 435),
                ]),
              });
            }

            this.menuItems.push({
              name: "VLAMO",
              route: createRoutePath([AppPaths.Organization, OrganizationPaths.FederationInfo]),
              icon: "fa-chart-network",
              subItems: vlamoSubItems,
            });
          }
          this.menuItems.push(
            {
              name: "Leden",
              icon: "fa-users",
              route: createRoutePath([AppPaths.Members, MembersPaths.Start]),
              subItems: [
                {
                  name: "Snel starten",
                  route: createRoutePath([AppPaths.Members, MembersPaths.Start]),
                },
                {
                  name: "Wachtlijst beheren",
                  route: createRoutePath([AppPaths.Members, MembersPaths.WaitingList]),
                },
                {
                  name: "Leden beheren",
                  route: createRoutePath([AppPaths.Members, MembersPaths.Overview]),
                },
                {
                  name: "Nieuw lid inschrijven",
                  route: createRoutePath([AppPaths.Members, MembersPaths.Register]),
                },
                {
                  name: "Leden importeren",
                  route: createRoutePath([AppPaths.Members, MembersPaths.Import]),
                },
                {
                  name: "Ledengroepen beheren",
                  route: createRoutePath([AppPaths.Members, MembersPaths.Teams]),
                },
                {
                  name: "Vaardigheden",
                  route: createRoutePath([AppPaths.Persons, PersonsPaths.Skills]),
                },
                {
                  name: "Doorstroming",
                  route: createRoutePath([AppPaths.Members, MembersPaths.TeamFlow]),
                },
                {
                  name: "Attest Ziekenfonds",
                  route: createRoutePath(AppPaths.Dashboard),
                },
                // {
                //   name: 'Voorkeuren en instellingen',
                //   route: '/welkom',
                // },
              ],
            },
            {
              name: "Vrijwilligers",
              icon: "fa-handshake-angle",
              route: createRoutePath([AppPaths.Volunteers, VolunteersPaths.Start]),
              subItems: [
                {
                  name: "Snel starten",
                  route: createRoutePath([AppPaths.Volunteers, VolunteersPaths.Start]),
                },
                {
                  name: "Kandidaten beheren",
                  route: createRoutePath([AppPaths.Volunteers, VolunteersPaths.Candidates]),
                },
                {
                  name: "Vrijwilligers beheren",
                  route: createRoutePath([AppPaths.Volunteers, VolunteersPaths.Overview]),
                },
                {
                  name: "Vrijwilliger toevoegen",
                  route: createRoutePath([AppPaths.Volunteers, VolunteersPaths.VolunteerAdd]),
                },
                {
                  name: "Vrijwilligers importeren",
                  route: createRoutePath([AppPaths.Volunteers, VolunteersPaths.Import]),
                },
                {
                  name: "Vrijwilligersgroepen beheren",
                  route: createRoutePath([AppPaths.Volunteers, VolunteersPaths.Groups]),
                },
                {
                  name: "Functies beheren",
                  route: createRoutePath([AppPaths.Volunteers, VolunteersPaths.Functions]),
                },
                {
                  name: "Interesses beheren",
                  route: createRoutePath([AppPaths.Tasks, TasksPaths.Interests]),
                },
                {
                  name: "Afsprakennota's beheren",
                  route: createRoutePath([AppPaths.Volunteers, VolunteersPaths.AgreementNotes]),
                },
                {
                  name: "Rapportage en statistieken",
                  route: createRoutePath([AppPaths.Volunteers, VolunteersPaths.Statistics]),
                },
              ],
            },
            {
              name: "Taken en interesses",
              route: createRoutePath([AppPaths.Tasks, TasksPaths.Start]),
              icon: "fa-tasks",
              //federationIds: [2, 1013],
              //subscriptionTypeIds: [1],
              subItems: [
                {
                  name: "Snel starten",
                  route: createRoutePath([AppPaths.Tasks, TasksPaths.Start]),
                },
                {
                  name: "Interesses beheren",
                  route: createRoutePath([AppPaths.Tasks, TasksPaths.Interests]),
                },
                {
                  name: "Personen importeren",
                  route: createRoutePath([AppPaths.Persons, PersonsPaths.Import]),
                },
                {
                  name: "Polsen naar interesses",
                  route: createRoutePath([AppPaths.Persons, PersonsPaths.IntrestCheck]),
                },
                {
                  name: "Taken beheren",
                  route: createRoutePath([AppPaths.Tasks, TasksPaths.Overview]),
                },
                //{
                //  name: 'Sjablonen beheren',
                //  route: '/tasks/sjablonen',
                //},
                {
                  name: "Uitnodigen en werven",
                  route: createRoutePath([AppPaths.Tasks, TasksPaths.Invite]),
                },
                {
                  name: "Uitgenodigde kandidaten",
                  route: createRoutePath([AppPaths.Tasks, TasksPaths.InvitedPersons]),
                },
              ],
            },
            {
              name: "Vacatures",
              route: createRoutePath([AppPaths.Jobs, JobsPaths.Start]),
              icon: "fa-ballot",
              subItems: [
                {
                  name: "Vacatures beheren",
                  route: createRoutePath(AppPaths.Jobs),
                },
                {
                  name: "Vacature toevoegen",
                  route: createRoutePath([AppPaths.Jobs, JobsPaths.Add]),
                },
                {
                  name: "Sjablonen beheren",
                  route: createRoutePath([AppPaths.Jobs, JobsPaths.Templates]),
                },
                {
                  name: "Widgets beheren",
                  route: createRoutePath([AppPaths.Widgets, WidgetsPaths.Overview]),
                },
              ],
            },
            {
              name: "Kostenvergoedingen",
              icon: "fa-money-bill",
              subItems: [
                {
                  name: "Kosten beheren",
                  route: createRoutePath([AppPaths.Expenses, ExpensesPaths.List]),
                },
                {
                  name: "Kostennota's",
                  route: createRoutePath([AppPaths.Expenses, ExpensesPaths.Reports]),
                },
                {
                  name: "Kostencategorieën",
                  route: createRoutePath([AppPaths.Expenses, ExpensesPaths.Categories]),
                },
                {
                  name: "Vrijwilligersregister",
                  route: createRoutePath([AppPaths.Volunteers, VolunteersPaths.Register]),
                },
              ],
            },
            {
              name: "Boekhouding",
              route: createRoutePath([AppPaths.Accounting, AccountingPaths.Start]),
              icon: "fa-calculator",
              subItems: [
                //            {
                //              name: 'Dashboard',
                //              route: '/accounting/start',
                //            },
                {
                  name: "Peppol-inbox",
                  route: createRoutePath([AppPaths.Accounting, AccountingPaths.PeppolInbox]),
                },
                {
                  name: "Verrichting zoeken",
                  route: createRoutePath([
                    AppPaths.Accounting,
                    AccountingPaths.ReceiptsAndExpenses,
                  ]),
                },
                {
                  name: "Verrichting toevoegen",
                  route: createRoutePath([AppPaths.Accounting, AccountingPaths.TransactionAdd]),
                },
                {
                  name: "Geldoverdracht toevoegen",
                  route: createRoutePath([
                    AppPaths.Accounting,
                    AccountingPaths.TransactionAddForType(9),
                  ]),
                },
                {
                  name: "Verrichtingen importeren",
                  route: createRoutePath([AppPaths.Accounting, AccountingPaths.Import]),
                },
                {
                  name: "Vorderingen",
                  route: createRoutePath([AppPaths.Accounting, AccountingPaths.Claims]),
                },
                {
                  name: "Schulden",
                  route: createRoutePath([AppPaths.Accounting, AccountingPaths.Debts]),
                },
                {
                  name: "Ontvangen creditnota's",
                  route: createRoutePath([
                    AppPaths.Accounting,
                    AccountingPaths.ReceivedCreditNotes, //AccountingPaths.TransactionAddForWorkYearAndType(62193, 7),
                  ]),
                },
                {
                  name: "Uitgereikte creditnota's",
                  route: createRoutePath([
                    AppPaths.Accounting,
                    AccountingPaths.GivenCreditNotes, //AccountingPaths.TransactionAddForWorkYearAndType(62193, 6),
                  ]),
                },
                {
                  name: "Financiële status",
                  route: createRoutePath([AppPaths.Accounting, AccountingPaths.FinancialStatus]),
                },
                {
                  name: "Financiële rapporten",
                  route: createRoutePath([AppPaths.Accounting, AccountingPaths.Reports]),
                },
                {
                  name: "Begroting",
                  route: createRoutePath([AppPaths.Accounting, AccountingPaths.Budgets]),
                },
                {
                  name: "Staat van Vermogen",
                  route: createRoutePath([
                    AppPaths.Accounting,
                    AccountingPaths.StateOfAssets,
                    StateOfAssetsPaths.Overview,
                  ]),
                },
                //                {
                //                  name: 'Boekhoudperiode afsluiten',
                //                  route: '/accounting/start',
                //                },
                {
                  name: "Voorkeuren en instellingen",
                  route: createRoutePath([AppPaths.Accounting, AccountingPaths.Start]),
                },
              ],
            },
            {
              name: "Btw",
              icon: "fa-percent",
              isVat: false,
              subItems: [
                {
                  name: "Peppol-inbox",
                  route: createRoutePath([AppPaths.Accounting, AccountingPaths.PeppolInbox]),
                },
                {
                  name: "Dagontvangstenboek",
                  route: createRoutePath([AppPaths.Accounting, AccountingPaths.DayReceipts]),
                },
              ],
            },
            {
              name: "Btw",
              icon: "fa-percent",
              isVat: true,
              subItems: [
                //            {
                //              name: 'Dashboard',
                //              route: '/accounting/start',
                //            },
                {
                  name: "Peppol-inbox",
                  route: createRoutePath([AppPaths.Accounting, AccountingPaths.PeppolInbox]),
                },
                {
                  name: "Dagontvangstenboek",
                  route: createRoutePath([AppPaths.Accounting, AccountingPaths.DayReceipts]),
                },
                {
                  name: "Verkoopfacturen",
                  route: createRoutePath([AppPaths.Accounting, AccountingPaths.SaleInvoices]),
                },
                {
                  name: "Aankoopfacturen",
                  route: createRoutePath([AppPaths.Accounting, AccountingPaths.PurchaseInvoices]),
                },
                {
                  name: "Ontvangen creditnota's",
                  route: createRoutePath([
                    AppPaths.Accounting,
                    AccountingPaths.ReceivedCreditNotes, //AccountingPaths.TransactionAddForWorkYearAndType(62193, 7),
                  ]),
                },
                {
                  name: "Uitgereikte creditnota's",
                  route: createRoutePath([
                    AppPaths.Accounting,
                    AccountingPaths.GivenCreditNotes, //AccountingPaths.TransactionAddForWorkYearAndType(62193, 6),
                  ]),
                },
                {
                  name: "Btw-kwartaalaangifte",
                  route: createRoutePath([AppPaths.Accounting, AccountingPaths.VatDeclration]),
                },
                {
                  name: "Klantenlisting",
                  route: createRoutePath([AppPaths.Accounting, AccountingPaths.ClientListing]),
                },
                {
                  name: "Voorkeuren en instellingen",
                  route: createRoutePath([AppPaths.Accounting, AccountingPaths.Start]),
                },
              ],
            },
            {
              name: "Activiteiten",
              route: createRoutePath([AppPaths.Activities, ActivityPaths.List]),
              icon: "fa-calendar-days",
              subItems: [
                {
                  name: "Activiteiten beheren",
                  route: createRoutePath([AppPaths.Activities, ActivityPaths.List]),
                },
              ],
            },
            {
              name: "Contacten",
              icon: "fa-address-book",
              subItems: [
                {
                  name: "Alle personen",
                  route: createRoutePath(AppPaths.Persons),
                },
                {
                  name: "Alle organisaties",
                  route: createRoutePath(AppPaths.Addresses),
                },
              ],
            },
            {
              name: "Gesprekken",
              icon: "fa-comments",
              subItems: [
                {
                  name: "Gesprekken beheren",
                  route: createRoutePath([AppPaths.Meetings, MeetingsPaths.Overview]),
                },
                {
                  name: "Gesprek toevoegen",
                  route: createRoutePath([AppPaths.Meetings, MeetingsPaths.Add]),
                },
                {
                  name: "Sjablonen beheren",
                  route: createRoutePath([AppPaths.Meetings, MeetingsPaths.Templates]),
                },
              ],
            },
            {
              name: "Communicatie",
              icon: "fa-comment",
              subItems: [
                /*{
                      name: 'Nieuwsbrieven',
                      route: '/communication/nieuwsbrief/overzicht',
                    },
                    // {
                    //   name: 'Mailings',
                    //   route: '/communication/nieuwsbrief/bewerken',
                    // },*/
                {
                  name: "E-mailsjablonen",
                  route: createRoutePath([
                    AppPaths.Communication,
                    CommunicationPaths.MailTemplateList,
                  ]),
                },
              ],
            },
            {
              name: "Formulieren",
              route: createRoutePath(AppPaths.Forms),
              icon: "fa-memo-pad",
              subItems: [
                {
                  name: "Inschrijvingsformulieren",
                  route: createRoutePath([AppPaths.Forms, FormsPaths.Overview]),
                },
              ],
            },
            {
              name: "Vzw-Helper",
              route: createRoutePath([AppPaths.VzwHelper, VzwHelperPaths.Start]),
              icon: "fa-shield-check",
            }
            // {
            //   name: 'Instellingen',
            //   route: '/settings/settings',
            //   icon: 'fa-gear',
            //   subItems: [
            //     {
            //       name: 'Leden',
            //       route: '/settings/leden',
            //     },
            //     {
            //       name: 'Vrijwilligers',
            //       route: '/settings/vrijwilligers',
            //     },
            //   ],
            // },
          );
        });
      }

      // ZWEMFED
      if (this.global.subscription.OrganizationId == 1535) {
        this.menuItems.push(
          {
            name: "Leden",
            icon: "fa-users",
            subItems: [
              {
                name: "Leden van de zwemfed",
                route: createRoutePath([AppPaths.Persons, PersonsPaths.OverviewZwemfed]),
              },
              {
                name: "VOTAS-leden",
                route: createRoutePath([AppPaths.Persons, PersonsPaths.VotasMembers(1)]),
              },
              {
                name: "Aangesloten Clubs",
                route: createRoutePath([AppPaths.Federation, FederationPaths.Overview(38)]),
              },
            ],
          },
          {
            name: "Vrijwilligers",
            icon: "fa-handshake-angle",
            route: createRoutePath([AppPaths.Volunteers, VolunteersPaths.Start]),
            subItems: [
              {
                name: "Snel starten",
                route: createRoutePath([AppPaths.Volunteers, VolunteersPaths.Start]),
              },
              {
                name: "Kandidaten beheren",
                route: createRoutePath([AppPaths.Volunteers, VolunteersPaths.Candidates]),
              },
              {
                name: "Vrijwilligers beheren",
                route: createRoutePath([AppPaths.Volunteers, VolunteersPaths.Overview]),
              },
              {
                name: "Vrijwilliger toevoegen",
                route: createRoutePath([AppPaths.Volunteers, VolunteersPaths.VolunteerAdd]),
              },
              {
                name: "Vrijwilligersgroepen beheren",
                route: createRoutePath([AppPaths.Volunteers, VolunteersPaths.Groups]),
              },
              {
                name: "Functies beheren",
                route: createRoutePath([AppPaths.Volunteers, VolunteersPaths.Functions]),
              },
            ],
          },
          {
            name: "Taken en interesses",
            route: createRoutePath([AppPaths.Tasks, TasksPaths.Start]),
            icon: "fa-tasks",
            //federationIds: [2, 1013],
            subItems: [
              {
                name: "Snel starten",
                route: createRoutePath([AppPaths.Tasks, TasksPaths.Start]),
              },
              {
                name: "Interesses beheren",
                route: createRoutePath([AppPaths.Tasks, TasksPaths.Interests]),
              },
              {
                name: "Personen importeren",
                route: createRoutePath([AppPaths.Persons, PersonsPaths.Import]),
              },
              {
                name: "Polsen naar interesses",
                route: createRoutePath([AppPaths.Persons, PersonsPaths.IntrestCheck]),
              },
              {
                name: "Taken beheren",
                route: createRoutePath([AppPaths.Tasks, TasksPaths.Overview]),
              },
              //{
              //  name: 'Sjablonen beheren',
              //  route: '/tasks/sjablonen',
              //},
              {
                name: "Uitnodigen en werven",
                route: createRoutePath([AppPaths.Tasks, TasksPaths.Invite]),
              },
              {
                name: "Uitgenodigde kandidaten",
                route: createRoutePath([AppPaths.Tasks, TasksPaths.InvitedPersons]),
              },
            ],
          },
          {
            name: "Kostenvergoedingen",
            icon: "fa-money-bill",
            subItems: [
              {
                name: "Kosten beheren",
                route: createRoutePath([AppPaths.Expenses, ExpensesPaths.List]),
              },
              {
                name: "Kostennota's",
                route: createRoutePath([AppPaths.Expenses, ExpensesPaths.Reports]),
              },
              {
                name: "Kostencategorieën",
                route: createRoutePath([AppPaths.Expenses, ExpensesPaths.Categories]),
              },
              {
                name: "Vrijwilligersregister",
                route: createRoutePath([AppPaths.Volunteers, VolunteersPaths.Register]),
              },
            ],
          },
          {
            name: "Vacatures",
            icon: "fa-ballot",
            subItems: [
              {
                name: "Vacatures beheren",
                route: createRoutePath(AppPaths.Jobs),
              },
              {
                name: "Vacature toevoegen",
                route: createRoutePath([AppPaths.Jobs, JobsPaths.Add]),
              },
              {
                name: "Sjablonen beheren",
                route: createRoutePath([AppPaths.Jobs, JobsPaths.Templates]),
              },
              {
                name: "Widgets beheren",
                route: createRoutePath([AppPaths.Widgets, WidgetsPaths.Overview]),
              },
            ],
          }
        );
      }

      // VLAAMS STEUNPUNT VRIJWILLIGERSWERK
      if (this.global.subscription.OrganizationId == 11458) {
        this.menuItems.push(
          {
            name: "Verenigingen",
            icon: "",
            subItems: [
              {
                name: "Verenigingen",
                route: createRoutePath([AppPaths.Federation, FederationPaths.Overview(57)]),
              },
            ],
          },
          {
            name: "Vrijwilligers",
            icon: "fa-handshake-angle",
            subItems: [
              {
                name: "Kandidaten beheren",
                route: createRoutePath([AppPaths.Volunteers, VolunteersPaths.Candidates]),
              },
              {
                name: "Vrijwilligers beheren",
                route: createRoutePath([AppPaths.Volunteers, VolunteersPaths.Overview]),
              },
            ],
          },
          {
            name: "Kostenvergoedingen",
            icon: "fa-money-bill",
            subItems: [
              {
                name: "Kosten beheren",
                route: createRoutePath([AppPaths.Expenses, ExpensesPaths.List]),
              },
              {
                name: "Kostennota's",
                route: createRoutePath([AppPaths.Expenses, ExpensesPaths.Reports]),
              },
              {
                name: "Kostencategorieën",
                route: createRoutePath([AppPaths.Expenses, ExpensesPaths.Categories]),
              },
              {
                name: "Vrijwilligersregister",
                route: createRoutePath([AppPaths.Volunteers, VolunteersPaths.Register]),
              },
            ],
          },
          {
            name: "Vacatures",
            icon: "fa-ballot",
            subItems: [
              {
                name: "Vacatures beheren",
                route: createRoutePath(AppPaths.Jobs),
              },
              {
                name: "Vacature toevoegen",
                route: createRoutePath([AppPaths.Jobs, JobsPaths.Add]),
              },
              {
                name: "Sjablonen beheren",
                route: createRoutePath([AppPaths.Jobs, JobsPaths.Templates]),
              },
            ],
          },
          {
            name: "Widgets",
            icon: "fa-grid-2",
            subItems: [
              {
                name: "Eigen widgets beheren",
                route: createRoutePath([AppPaths.Widgets, WidgetsPaths.Overview]),
              },
              {
                name: "Widget teksten aanpassen",
                route: createRoutePath([AppPaths.Widgets, WidgetsPaths.EditTexts]),
              },
            ],
          },
          {
            name: "vrijwilligerswerk.be",
            icon: "fa-arrow-pointer",
            subItems: [
              {
                name: "Vacatures beheren",
                route: createRoutePath([
                  AppPaths.Vrijwilligerswerk,
                  VrijwilligerswerkPaths.Vacatures,
                ]),
              },
              {
                name: "Verenigingen",
                route: createRoutePath([AppPaths.Federation, FederationPaths.Overview(36)]),
                queryParams: { platform: 1 },
              },
              {
                name: "In de kijker",
                route: createRoutePath([AppPaths.Jobs, JobsPaths.Spotlight]),
              },
            ],
          },
          {
            name: "wéldoeners.be",
            icon: "fa-arrow-pointer",
            subItems: [
              {
                name: "Vacatures goedkeuren",
                route: createRoutePath([AppPaths.Weldoeners, WeldoenersPaths.Approve]),
              },
              {
                name: "Vacatures beheren",
                route: createRoutePath([AppPaths.Weldoeners, WeldoenersPaths.Vacatures]),
              },
              {
                name: "Wéldoeners beheren",
                route: createRoutePath([AppPaths.Weldoeners, WeldoenersPaths.Volunteers]),
              },
              {
                name: "Verenigingen",
                route: createRoutePath([AppPaths.Federation, FederationPaths.Overview(36)]),
                queryParams: { platform: 2 },
              },
            ],
          },
          {
            name: "V-Wijzer",
            route: createRoutePath([AppPaths.VWijzer]),
            icon: "fa-building-memo",
            subItems: [
              {
                name: "Dossiers V-Wijzer",
                route: createRoutePath([AppPaths.Forms, FormsPaths.FormDocumentList(442)]),
              },
              {
                name: "Dossiers Quickscan",
                route: createRoutePath([AppPaths.Forms, FormsPaths.FormDocumentList(453)]),
              },
            ],
          }
        );
      }

      // STAD GENK (= subsidiedossiers)
      if (this.global.subscription.OrganizationId == 12148) {
        this.menuItems.push(
          {
            name: "Mijn organisatie",
            route: createRoutePath(AppPaths.Organization),
            icon: "fa-sitemap",
          },
          {
            name: "Verenigingen",
            icon: "fa-users",
            subItems: [
              {
                name: "Socioculturele verenigingen",
                route: createRoutePath([AppPaths.Federation, FederationPaths.Overview(40)]),
              },
              {
                name: "Sportclubs",
                route: createRoutePath([AppPaths.Federation, FederationPaths.Overview(39)]),
              },
              {
                name: "Jeugdverenigingen",
                route: createRoutePath([AppPaths.Federation, FederationPaths.Overview(41)]),
              },
            ],
          },
          {
            name: "Vrijwilligers",
            icon: "fa-handshake-angle",
            route: createRoutePath([AppPaths.Volunteers, VolunteersPaths.Start]),
            subItems: [
              {
                name: "Snel starten",
                route: createRoutePath([AppPaths.Volunteers, VolunteersPaths.Start]),
              },
              {
                name: "Kandidaten beheren",
                route: createRoutePath([AppPaths.Volunteers, VolunteersPaths.Candidates]),
              },
              {
                name: "Vrijwilligers beheren",
                route: createRoutePath([AppPaths.Volunteers, VolunteersPaths.Overview]),
              },
              {
                name: "Vrijwilligersgroepen beheren",
                route: createRoutePath([AppPaths.Volunteers, VolunteersPaths.Groups]),
              },
              {
                name: "Functies beheren",
                route: createRoutePath([AppPaths.Volunteers, VolunteersPaths.Functions]),
              },
            ],
          },
          {
            name: "Taken en interesses",
            route: createRoutePath([AppPaths.Tasks, TasksPaths.Start]),
            icon: "fa-tasks",
            //federationIds: [2, 1013],
            subItems: [
              {
                name: "Snel starten",
                route: createRoutePath([AppPaths.Tasks, TasksPaths.Start]),
              },
              {
                name: "Interesses beheren",
                route: createRoutePath([AppPaths.Tasks, TasksPaths.Interests]),
              },
              {
                name: "Personen importeren",
                route: createRoutePath([AppPaths.Persons, PersonsPaths.Import]),
              },
              {
                name: "Polsen naar interesses",
                route: createRoutePath([AppPaths.Persons, PersonsPaths.IntrestCheck]),
              },
              {
                name: "Taken beheren",
                route: createRoutePath([AppPaths.Tasks, TasksPaths.Overview]),
              },
              //{
              //  name: 'Sjablonen beheren',
              //  route: '/tasks/sjablonen',
              //},
              {
                name: "Uitnodigen en werven",
                route: createRoutePath([AppPaths.Tasks, TasksPaths.Invite]),
              },
              {
                name: "Uitgenodigde kandidaten",
                route: createRoutePath([AppPaths.Tasks, TasksPaths.InvitedPersons]),
              },
            ],
          },
          {
            name: "Kostenvergoedingen",
            icon: "fa-money-bill",
            subItems: [
              {
                name: "Kosten beheren",
                route: createRoutePath([AppPaths.Expenses, ExpensesPaths.List]),
              },
              {
                name: "Kostennota's",
                route: createRoutePath([AppPaths.Expenses, ExpensesPaths.Reports]),
              },
              {
                name: "Kostencategorieën",
                route: createRoutePath([AppPaths.Expenses, ExpensesPaths.Categories]),
              },
              {
                name: "Vrijwilligersregister",
                route: createRoutePath([AppPaths.Volunteers, VolunteersPaths.Register]),
              },
            ],
          },
          {
            name: "Vacatures",
            icon: "fa-ballot",
            subItems: [
              {
                name: "Vacatures beheren",
                route: createRoutePath(AppPaths.Jobs),
              },
              {
                name: "Vacature toevoegen",
                route: createRoutePath([AppPaths.Jobs, JobsPaths.Add]),
              },
              {
                name: "Sjablonen beheren",
                route: createRoutePath([AppPaths.Jobs, JobsPaths.Templates]),
              },
              {
                name: "Widgets beheren",
                route: createRoutePath([AppPaths.Widgets, WidgetsPaths.Overview]),
              },
            ],
          }
        );
      }

      // LOKAAL BESTUUR WUUSTWEZEL (= test-abonnement)
      if (this.global.subscription.OrganizationId == 26738) {
        this.menuItems.push(
          {
            name: "Mijn organisatie",
            route: createRoutePath(AppPaths.Organization),
            icon: "fa-sitemap",
          },
          {
            name: "Verenigingen",
            icon: "fa-users",
            subItems: [
              {
                name: "Socioculturele verenigingen",
                route: createRoutePath([AppPaths.Federation, FederationPaths.Overview(40)]),
              },
              {
                name: "Sportclubs",
                route: createRoutePath([AppPaths.Federation, FederationPaths.Overview(58)]),
              },
              {
                name: "Jeugdverenigingen",
                route: createRoutePath([AppPaths.Federation, FederationPaths.Overview(41)]),
              },
            ],
          },
          {
            name: "Vrijwilligers",
            icon: "fa-handshake-angle",
            route: createRoutePath([AppPaths.Volunteers, VolunteersPaths.Start]),
            subItems: [
              {
                name: "Snel starten",
                route: createRoutePath([AppPaths.Volunteers, VolunteersPaths.Start]),
              },
              {
                name: "Kandidaten beheren",
                route: createRoutePath([AppPaths.Volunteers, VolunteersPaths.Candidates]),
              },
              {
                name: "Vrijwilligers beheren",
                route: createRoutePath([AppPaths.Volunteers, VolunteersPaths.Overview]),
              },
              {
                name: "Vrijwilligersgroepen beheren",
                route: createRoutePath([AppPaths.Volunteers, VolunteersPaths.Groups]),
              },
              {
                name: "Functies beheren",
                route: createRoutePath([AppPaths.Volunteers, VolunteersPaths.Functions]),
              },
            ],
          },
          {
            name: "Taken en interesses",
            route: createRoutePath([AppPaths.Tasks, TasksPaths.Start]),
            icon: "fa-tasks",
            //federationIds: [2, 1013],
            subItems: [
              {
                name: "Snel starten",
                route: createRoutePath([AppPaths.Tasks, TasksPaths.Start]),
              },
              {
                name: "Interesses beheren",
                route: createRoutePath([AppPaths.Tasks, TasksPaths.Interests]),
              },
              {
                name: "Personen importeren",
                route: createRoutePath([AppPaths.Persons, PersonsPaths.Import]),
              },
              {
                name: "Polsen naar interesses",
                route: createRoutePath([AppPaths.Persons, PersonsPaths.IntrestCheck]),
              },
              {
                name: "Taken beheren",
                route: createRoutePath([AppPaths.Tasks, TasksPaths.Overview]),
              },
              //{
              //  name: 'Sjablonen beheren',
              //  route: '/tasks/sjablonen',
              //},
              {
                name: "Uitnodigen en werven",
                route: createRoutePath([AppPaths.Tasks, TasksPaths.Invite]),
              },
              {
                name: "Uitgenodigde kandidaten",
                route: createRoutePath([AppPaths.Tasks, TasksPaths.InvitedPersons]),
              },
            ],
          },
          {
            name: "Kostenvergoedingen",
            icon: "fa-money-bill",
            subItems: [
              {
                name: "Kosten beheren",
                route: createRoutePath([AppPaths.Expenses, ExpensesPaths.List]),
              },
              {
                name: "Kostennota's",
                route: createRoutePath([AppPaths.Expenses, ExpensesPaths.Reports]),
              },
              {
                name: "Kostencategorieën",
                route: createRoutePath([AppPaths.Expenses, ExpensesPaths.Categories]),
              },
              {
                name: "Vrijwilligersregister",
                route: createRoutePath([AppPaths.Volunteers, VolunteersPaths.Register]),
              },
            ],
          },
          {
            name: "Vacatures",
            icon: "fa-ballot",
            subItems: [
              {
                name: "Vacatures beheren",
                route: createRoutePath(AppPaths.Jobs),
              },
              {
                name: "Vacature toevoegen",
                route: createRoutePath([AppPaths.Jobs, JobsPaths.Add]),
              },
              {
                name: "Sjablonen beheren",
                route: createRoutePath([AppPaths.Jobs, JobsPaths.Templates]),
              },
              {
                name: "Widgets beheren",
                route: createRoutePath([AppPaths.Widgets, WidgetsPaths.Overview]),
              },
            ],
          }
        );
      }

      //        if(isDevMode() && subscription.OrganizationId != 17786) {
      //        add tasks menu item
      //          this.menuItems.push(
      //            {
      //            name: 'Taken',
      //            route: '/taken/start',
      //            icon: 'fa-tasks',
      //            subItems: [
      //              {
      //                name: 'Dashboard',
      //                route: '/taken/start',
      //              },
      //              {
      //                name: 'Interesses beheren',
      //                route: '/taken/interesses',
      //              },
      //              {
      //                name: 'Taken beheren',
      //                route: '/taken/overzicht',
      //              },
      //              {
      //                name: 'Sjablonen voor taken',
      //                route: '/tasks/sjablonen',
      //              },
      //              {
      //                name: 'Uitnodigen en werven',
      //                route: '/taken/uitnodigen',
      //              }
      //            ]
      //          })
      //        }
    }
  }

  getMenuItemByName(name: string): MenuItem {
    return this.menuItems.find((item) => item.name == name) || ({} as MenuItem);
  }

  getAccessKeyFromQueryString() {
    const url = new URL(window.location.href);
    const hash = url.hash;

    // Het pad en de querystring splitsen
    const [path, queryString] = hash.split("?");

    // De query parameters parsen
    const queryParams = new URLSearchParams(queryString);
    var accesskey = queryParams.get("accesskey");
    if (accesskey == null) accesskey = queryParams.get("accessKey");
    // De gewenste parameter ophalen
    return accesskey;
  }

  onClickOutsideSideNav(event: PointerEvent) {
    const target = event.target as HTMLElement;
    if (target.id !== "iconNavbarSidenav") {
      this.closeNavIfOpen();
    }
  }

  ngAfterViewInit(): void {
    // reageer op route-wijzigingen
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((ev) => {
        this.updatePublicOutlet(ev.urlAfterRedirects);
      });

    // eerstekans check (pagina reload of directe navigatie)
    this.updatePublicOutlet(this.router.url);
  }

  private updatePublicOutlet(url: string) {
    this.isErrorSolver = url.startsWith("/error-oplosser");
    if (this.isErrorSolver) {
      localStorage.setItem("disableSigninModal", "1");
    } else {
      localStorage.removeItem("disableSigninModal");
    }
  }
}
